import { useTranslation } from 'react-i18next';

import type { ApiNabanDeed } from '@izimi/api';
import { useIsMobile } from '@izimi/hooks';

import { Alert } from '../../../../alert';
import { CustomIcon } from '../../../../icon';
import { NabanCardListItem } from '../../../components/NabanCardListItem';
import { NabanDetailsListCard } from '../../../components/NabanDetailsListCard';
import { NabanDetailsListItem } from '../../../components/NabanDetailsListItem';
import { useNabanStoreSelectedAuthenticationMethod } from '../../../store';
import type { RenderDeedOptions } from '../../util/types';
import { nabanDetailsListCardContentProps } from './styles';

type Props = {
  document: ApiNabanDeed['statutesDocument'];
  napId: string;
  renderDeedOptions?: RenderDeedOptions;
};

export const NabanDeedStatutesDocuments = ({ document, napId, renderDeedOptions }: Props) => {
  const isMobile = useIsMobile();
  const { t } = useTranslation('naban');

  const selectedAuthenticationMethod = useNabanStoreSelectedAuthenticationMethod();

  return (
    <NabanDetailsListCard
      title={t('deed.statutesDocuments.title')}
      contentProps={{ ...nabanDetailsListCardContentProps, divider: undefined }}>
      <NabanCardListItem title={document?.title ?? ''}>
        <NabanDetailsListItem
          flex={2}
          title={t('deed.statutesDocuments.label.enterpriseNumber')}
          value={
            <>
              {document?.enterpriseNumber}
              {document?.statutesHistoryUrl && (
                <span>
                  {' ('}
                  <a
                    target='_blank'
                    rel='noopener noreferer noreferrer'
                    href={document.statutesHistoryUrl}>
                    {t('deed.statutesDocuments.historyLink.label')}
                    <CustomIcon
                      name='external-link-small'
                      color='neutral500'
                      display='inline-block'
                      ml={0.25}
                      mr={0.25}
                      verticalAlign='text-top'
                      transform='translateY(1px)'
                    />
                  </a>
                  )
                </span>
              )}
            </>
          }
          display={isMobile ? 'none' : 'flex'}
        />
        {renderDeedOptions && document?.documentToken
          ? renderDeedOptions({
              documentToken: document.documentToken,
              documentTitle: document.title ?? '',
              napId,
              authenticationModeToken: selectedAuthenticationMethod?.token,
              documentIdentifier: document.documentIdentifier,
              isEmbeddedDocument: false,
            })
          : null}
      </NabanCardListItem>
      {document?.moreRecentStatutesVersionExist && (
        <Alert
          status='warning'
          alertIcon={
            <CustomIcon name='warning' flexShrink={0} color='error500' secondaryColor='error100' />
          }
          alertDescription={t('deed.statutesDocuments.obsoleteVersionWarning')}
        />
      )}
    </NabanDetailsListCard>
  );
};
